const CSV_CACHE_TTL_MS = 5 * 60 * 1000
const csvCache = new Map<string, { rows: Record<string, string>[]; expiresAt: number }>()

const getCacheEntry = (key: string) => {
    const entry = csvCache.get(key)
    if (!entry) return undefined
    if (Date.now() > entry.expiresAt) {
        csvCache.delete(key)
        return undefined
    }
    return entry
}

// Splits a single CSV line on commas, honoring double-quoted fields and
// escaped quotes ("" → "). Sufficient for the redirect/SEO CSVs the site
// loads — RFC 4180 strict variants (newlines inside quotes) are not used here.
const splitCsvLine = (line: string): string[] => {
    const fields: string[] = []
    let buffer = ''
    let inQuotes = false
    for (let i = 0; i < line.length; i++) {
        const ch = line[i]
        if (inQuotes) {
            if (ch === '"' && line[i + 1] === '"') {
                buffer += '"'
                i++
            } else if (ch === '"') {
                inQuotes = false
            } else {
                buffer += ch
            }
        } else {
            if (ch === '"') {
                inQuotes = true
            } else if (ch === ',') {
                fields.push(buffer)
                buffer = ''
            } else {
                buffer += ch
            }
        }
    }
    fields.push(buffer)
    return fields
}

/**
 * Streams a CSV file's rows as objects keyed by the header row.
 *
 * Replaces Deno's `CsvParseStream({ skipFirstRow: true })`. Only HTTP URLs
 * are supported — Cloudflare Workers have no filesystem, so paths like
 * `redirects/foo.csv` must be served via fetch (Worker assets / CDN).
 */
export default async function* (csvUrl: string): AsyncGenerator<Record<string, string>> {
    const cached = getCacheEntry(csvUrl)
    if (cached) {
        for (const row of cached.rows) yield row
        return
    }

    if (!csvUrl.startsWith('http')) {
        console.warn(`[streamCSV] non-http source ignored: ${csvUrl}`)
        return
    }

    const response = await fetch(csvUrl)
    if (!response.body) throw new Error(`streamCSV: no body for ${csvUrl}`)

    const text = await response.text()
    const lines = text.split(/\r?\n/).filter((l) => l.length > 0)
    if (lines.length === 0) return

    const headers = splitCsvLine(lines[0])
    const rows: Record<string, string>[] = []

    for (let i = 1; i < lines.length; i++) {
        const fields = splitCsvLine(lines[i])
        const row: Record<string, string> = {}
        for (let j = 0; j < headers.length; j++) {
            row[headers[j]] = fields[j] ?? ''
        }
        rows.push(row)
        yield row
    }

    csvCache.set(csvUrl, { rows, expiresAt: Date.now() + CSV_CACHE_TTL_MS })
}
